import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, createElementVNode as _createElementVNode, createVNode as _createVNode, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-b84fb424"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "tooltip-content" }
const _hoisted_2 = { class: "tooltip-inner-layout" }
const _hoisted_3 = { class: "tooltip-text-container" }
const _hoisted_4 = { class: "close-button-container" }
const _hoisted_5 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", {
  class: "tooltip-arrow",
  "data-popper-arrow": ""
}, null, -1))

import {
  ref,
  computed,
  onMounted,
  onUnmounted,
  nextTick,
  watch
} from "vue";

import { createPopper, Instance } from "@popperjs/core";
import FeatherIcon from "@/components/atoms/other/FeatherIcon.vue";
import { authStore } from "@/store/auth";
import { ClosedTooltipPopupSelector } from "@/types/auth/ClosedTooltipPopup";

// Type
type TooltipPosition = "top" | "bottom";

// prop、emits定義

export default /*#__PURE__*/_defineComponent({
  __name: 'TooltipPopup',
  props: {
  /**
   * 吹き出しの位置
   * 'top' または 'bottom'
   */
  position: {
    type: String as () => TooltipPosition,
    default: "bottom",
    validator: (value: string) => ["top", "bottom"].includes(value)
  },
  /**
   * ターゲット要素のセレクタ
   * 指定された要素の中心に向けて吹き出しが表示されます
   */
  targetSelector: {
    type: String as () => ClosedTooltipPopupSelector,
    required: true
  }
},
  emits: ["close"],
  setup(__props, { emit: emits }) {

const props = __props;

// import




// refやcomputedなど
const tooltipContainer = ref<HTMLElement | null>(null);
const popperInstance = ref<Instance | null>(null);
const targetExists = ref(false);

const isVisible = computed(() => {
  return (
    // ログイン中のユーザーに対してのみ表示
    authStore.existToken &&
    // ターゲットが存在するか
    targetExists.value &&
    // 閉じた吹き出しポップアップのリストが初期化済みか
    authStore.isInitializedClosedTooltipPopupList &&
    // 閉じた吹き出しポップアップのリストにターゲットセレクタが含まれていないか
    !authStore.hasClosedTooltipPopup(props.targetSelector)
  );
});

// ライフサイクルフック
onMounted(() => {
  targetExists.value = hasTarget();
  if (isVisible.value) {
    nextTick(() => {
      initializePopper();
    });
  }
});

onUnmounted(() => {
  if (isVisible.value) {
    // 吹き出しがアンマウントされるときに表示状態であれば、
    // ユーザーが明示的に閉じなくても、そのポップアップを「既読」として記録する
    // これにより、ページ遷移などでポップアップが閉じられた場合でも状態が保持される
    authStore.addClosedTooltipPopup(props.targetSelector);
  }
  if (popperInstance.value) {
    popperInstance.value.destroy();
  }
  window.removeEventListener("scroll", handleScroll);
});

watch(isVisible, newValue => {
  if (newValue) {
    nextTick(initializePopper);
  } else if (popperInstance.value) {
    popperInstance.value.destroy();
    popperInstance.value = null;
  }
});

watch(
  () => props.targetSelector,
  () => {
    targetExists.value = hasTarget();
    if (isVisible.value) {
      if (popperInstance.value) {
        popperInstance.value.destroy();
      }
      nextTick(initializePopper);
    }
  }
);

watch(
  () => props.position,
  () => {
    if (isVisible.value && popperInstance.value) {
      popperInstance.value.destroy();
      nextTick(initializePopper);
    }
  }
);

// 関数群
/**
 * ターゲット要素が存在するかどうか
 */
function hasTarget(): boolean {
  if (!props.targetSelector) {
    return false;
  }
  return !!document.querySelector(props.targetSelector);
}

/**
 * Popperの初期化
 */
function initializePopper() {
  if (!props.targetSelector || !tooltipContainer.value || !targetExists.value) {
    return;
  }

  const targetElement = document.querySelector(props.targetSelector);
  if (!targetElement) return;

  popperInstance.value = createPopper(targetElement, tooltipContainer.value, {
    placement: props.position,
    modifiers: [
      {
        name: "offset",
        options: {
          offset: [0, 12]
        }
      },
      {
        name: "arrow",
        options: {
          element: tooltipContainer.value?.querySelector(".tooltip-arrow"),
          padding: 5
        }
      },
      {
        name: "computeStyles",
        options: {
          gpuAcceleration: false
        }
      },
      {
        name: "preventOverflow",
        options: {
          padding: { top: 10, bottom: 10, left: 10, right: 10 },
          // 位置の自動変更を無効化
          altAxis: false,
          // 位置固定
          altBoundary: true
        }
      },
      // 位置を固定するための追加モディファイア
      {
        name: "flip",
        options: {
          fallbackPlacements: [], // 代替位置を使用しない
          allowedAutoPlacements: [props.position] // 指定した位置のみ許可
        }
      }
    ],
    strategy: "fixed" // 固定位置戦略を使用
  });

  // スクロールイベントリスナーを追加
  window.addEventListener("scroll", handleScroll);

  // ポップアップの位置を更新
  setTimeout(() => {
    if (popperInstance.value) {
      popperInstance.value.update();
    }
  }, 0);
}

/**
 * スクロールイベントハンドラ
 * スクロール時にポップアップの位置を更新
 */
function handleScroll() {
  if (popperInstance.value) {
    popperInstance.value.update();
  }
}

/**
 * 吹き出しを閉じる
 */
function handleClose() {
  authStore.addClosedTooltipPopup(props.targetSelector);
  emits("close");
}

return (_ctx: any,_cache: any) => {
  return (isVisible.value)
    ? (_openBlock(), _createElementBlock("div", {
        key: 0,
        ref_key: "tooltipContainer",
        ref: tooltipContainer,
        class: _normalizeClass(["tooltip-container", `tooltip-${__props.position}`])
      }, [
        _createElementVNode("div", _hoisted_1, [
          _createElementVNode("div", _hoisted_2, [
            _createElementVNode("div", _hoisted_3, [
              _renderSlot(_ctx.$slots, "default")
            ]),
            _createTextVNode(),
            _createElementVNode("div", _hoisted_4, [
              _createVNode(FeatherIcon, {
                icon: "x",
                size: "18",
                class: "close-icon",
                onClick: handleClose
              })
            ])
          ])
        ]),
        _createTextVNode(),
        _hoisted_5
      ], 2))
    : _createCommentVNode("", true)
}
}

})